import React, { useState } from 'react';
import { Link, usePage, router } from '@inertiajs/react';
import { cn } from '../../utils/cn';
import {
  Home,
  FileText,
  Users,
  Package,
  DollarSign,
  Banknote,
  AlertTriangle,
  Settings,
  LogOut,
  PanelLeft,
  Menu,
  ChevronsUpDown,
  ChevronRight,
  Moon,
  Sun,
  Link2,
  KeyRound,
  Bell,
  Palette,
  CreditCard,
} from 'lucide-react';
import { Avatar } from '../Avatar';
import { Tooltip } from '../Tooltip';
import { Dropdown } from '../Dropdown';
import {
  admin_root_path,
  admin_applications_path,
  admin_affiliates_path,
  admin_campaigns_path,
  admin_commissions_path,
  admin_payouts_path,
  admin_fraud_root_path,
  admin_settings_root_path,
  admin_settings_webhooks_path,
  admin_settings_api_keys_path,
  admin_settings_event_settings_path,
  admin_settings_theme_settings_path,
  admin_settings_paypal_settings_path,
  admin_sign_out_path,
} from '../../../lib/routes';
import { useFlash } from './useFlash';
import { useTheme } from '../../hooks/useTheme';
const navItems = [
  { label: 'Dashboard', href: admin_root_path(), icon: Home },
  { label: 'Applications', href: admin_applications_path(), icon: FileText },
  { label: 'Affiliates', href: admin_affiliates_path(), icon: Users },
  { label: 'Campaigns', href: admin_campaigns_path(), icon: Package },
  { label: 'Commissions', href: admin_commissions_path(), icon: DollarSign },
  { label: 'Payouts', href: admin_payouts_path(), icon: Banknote },
  { label: 'Fraud', href: admin_fraud_root_path(), icon: AlertTriangle },
  {
    label: 'Settings',
    href: admin_settings_root_path(),
    icon: Settings,
    children: [
      { label: 'General', href: admin_settings_root_path(), icon: Settings },
      { label: 'Webhooks', href: admin_settings_webhooks_path(), icon: Link2 },
      { label: 'API Keys', href: admin_settings_api_keys_path(), icon: KeyRound, superAdminOnly: true },
      { label: 'Events', href: admin_settings_event_settings_path(), icon: Bell, superAdminOnly: true },
      { label: 'Theme', href: admin_settings_theme_settings_path(), icon: Palette, superAdminOnly: true },
      { label: 'PayPal', href: admin_settings_paypal_settings_path(), icon: CreditCard, superAdminOnly: true },
    ],
  },
];

const COLLAPSED_KEY = 'admin_sidebar_collapsed';

export function AdminLayout({ children }) {
  const { props, url } = usePage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(COLLAPSED_KEY) === 'true';
  });
  const appName = props.app_name ?? 'Speakflow';
  const admin = props.current_admin;
  const isSuperAdmin = admin?.role === 'super_admin';
  useFlash();
  const { isDark, setTheme, darkOnly } = useTheme();
  const supportsDark = props.theme_supports_dark !== false;
  const showModeToggle = !darkOnly && supportsDark;

  function toggleCollapsed() {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(COLLAPSED_KEY, String(next));
      return next;
    });
  }

  function iconColor(isActive) {
    return isActive
      ? 'var(--color-fg-brand)'
      : 'var(--color-sidebar-fg-tertiary, var(--color-fg-tertiary))';
  }

  const filteredNav = navItems.filter((item) => !item.superAdminOnly || isSuperAdmin);
  const isOnSettingsPage = url.startsWith(admin_settings_root_path());
  const [settingsOpen, setSettingsOpen] = useState(isOnSettingsPage);

  const sidebarWidth = collapsed ? 'w-16' : 'w-64';
  const mainPadding = collapsed ? 'lg:pl-16' : 'lg:pl-64';

  return (
    <div className="min-h-screen bg-surface-secondary text-fg antialiased">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 z-40 h-full transform transition-all duration-200 lg:translate-x-0',
          sidebarWidth,
          collapsed && 'overflow-visible',
          sidebarOpen ? 'translate-x-0 w-64' : '-translate-x-full',
        )}
        style={{
          backgroundColor: 'var(--color-sidebar-bg, var(--color-surface))',
          borderRight: '1px solid var(--color-sidebar-border, var(--color-edge-strong))',
        }}
      >
        <div
          className={cn('flex items-center h-16', collapsed ? 'justify-center px-2 gap-0' : 'justify-between px-6')}
          style={{ borderBottom: '1px solid var(--color-sidebar-border, var(--color-edge-strong))' }}
        >
          {collapsed ? (
            <Tooltip content={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} position="right">
              <button
                type="button"
                onClick={toggleCollapsed}
                className="hidden lg:flex items-center justify-center w-8 h-8 rounded-md transition-colors"
                style={{ color: 'var(--color-sidebar-fg-tertiary, var(--color-fg-tertiary))' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-sidebar-hover, var(--color-surface-muted))'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
              >
                <PanelLeft size={16} strokeWidth={1.5} />
              </button>
            </Tooltip>
          ) : (
            <>
              <span
                className="text-lg font-semibold tracking-tight"
                style={{ color: 'var(--color-sidebar-fg, var(--color-fg))' }}
              >
                {appName}
              </span>
              <button
                type="button"
                onClick={toggleCollapsed}
                className="hidden lg:flex items-center justify-center w-7 h-7 rounded-md transition-colors"
                style={{ color: 'var(--color-sidebar-fg-tertiary, var(--color-fg-tertiary))' }}
                title="Collapse sidebar"
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-sidebar-hover, var(--color-surface-muted))'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
              >
                <PanelLeft size={16} strokeWidth={1.5} />
              </button>
            </>
          )}
        </div>

        <nav
          className={cn('space-y-1', collapsed ? 'p-2 overflow-visible' : 'p-4 overflow-y-auto')}
          style={{
            maxHeight: 'calc(100vh - 8rem)',
          }}
        >
          {filteredNav.map((item) => {
            const isActive = item.href === admin_root_path()
              ? url === item.href || url === item.href + '/'
              : url.startsWith(item.href);
            const Icon = item.icon;

            // Items with children render as a collapsible section
            if (item.children) {
              const filteredChildren = item.children.filter(
                (child) => !child.superAdminOnly || isSuperAdmin,
              );
              const isExpanded = settingsOpen || isOnSettingsPage;

              if (collapsed) {
                // When collapsed, show just the icon with tooltip
                return (
                  <Tooltip key={item.label} content={item.label} position="right">
                    <Link
                      href={item.href}
                      className={cn(
                        'flex items-center rounded-lg text-sm font-medium transition-colors group justify-center px-2 py-2',
                      )}
                      style={{
                        color: isActive
                          ? 'var(--color-sidebar-fg, var(--color-fg))'
                          : 'var(--color-sidebar-fg-secondary, var(--color-fg-secondary))',
                        backgroundColor: isActive
                          ? 'var(--color-sidebar-active, var(--color-surface-muted))'
                          : undefined,
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.color = 'var(--color-sidebar-fg, var(--color-fg))';
                          e.currentTarget.style.backgroundColor = 'var(--color-sidebar-hover, var(--color-surface-muted))';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.color = 'var(--color-sidebar-fg-secondary, var(--color-fg-secondary))';
                          e.currentTarget.style.backgroundColor = '';
                        }
                      }}
                    >
                      <Icon
                        size={20}
                        strokeWidth={1.5}
                        className="shrink-0"
                        style={{ color: iconColor(isActive) }}
                      />
                    </Link>
                  </Tooltip>
                );
              }

              return (
                <div key={item.label}>
                  {/* Parent toggle button */}
                  <button
                    type="button"
                    onClick={() => setSettingsOpen((prev) => !prev)}
                    className={cn(
                      'flex items-center w-full rounded-lg text-sm font-medium transition-colors gap-3 px-3 py-2',
                    )}
                    style={{
                      color: isActive
                        ? 'var(--color-sidebar-fg, var(--color-fg))'
                        : 'var(--color-sidebar-fg-secondary, var(--color-fg-secondary))',
                      backgroundColor: isActive && !isExpanded
                        ? 'var(--color-sidebar-active, var(--color-surface-muted))'
                        : undefined,
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive || isExpanded) {
                        e.currentTarget.style.color = 'var(--color-sidebar-fg, var(--color-fg))';
                        e.currentTarget.style.backgroundColor = 'var(--color-sidebar-hover, var(--color-surface-muted))';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive || isExpanded) {
                        e.currentTarget.style.color = isActive
                          ? 'var(--color-sidebar-fg, var(--color-fg))'
                          : 'var(--color-sidebar-fg-secondary, var(--color-fg-secondary))';
                        e.currentTarget.style.backgroundColor = isActive && !isExpanded
                          ? 'var(--color-sidebar-active, var(--color-surface-muted))'
                          : '';
                      }
                    }}
                  >
                    <Icon
                      size={20}
                      strokeWidth={1.5}
                      className="shrink-0"
                      style={{ color: iconColor(isActive) }}
                    />
                    <span className="flex-1 text-left">{item.label}</span>
                    <ChevronRight
                      size={16}
                      strokeWidth={1.5}
                      className={cn(
                        'shrink-0 transition-transform duration-200',
                        isExpanded && 'rotate-90',
                      )}
                      style={{
                        color: 'var(--color-sidebar-fg-tertiary, var(--color-fg-tertiary))',
                      }}
                    />
                  </button>

                  {/* Children */}
                  {isExpanded && (
                    <div className="mt-1 ml-3 space-y-0.5">
                      {filteredChildren.map((child) => {
                        const childIsActive =
                          child.href === admin_settings_root_path()
                            ? url === child.href || url === child.href + '/' || url === '/admin/settings'
                            : url.startsWith(child.href);
                        const ChildIcon = child.icon;
                        return (
                          <Link
                            key={child.label}
                            href={child.href}
                            className={cn(
                              'flex items-center gap-2.5 rounded-lg pl-5 pr-3 py-1.5 text-sm transition-colors',
                            )}
                            style={{
                              color: childIsActive
                                ? 'var(--color-sidebar-fg, var(--color-fg))'
                                : 'var(--color-sidebar-fg-secondary, var(--color-fg-secondary))',
                              fontWeight: childIsActive ? 500 : 400,
                              backgroundColor: childIsActive
                                ? 'var(--color-sidebar-active, var(--color-surface-muted))'
                                : undefined,
                            }}
                            onMouseEnter={(e) => {
                              if (!childIsActive) {
                                e.currentTarget.style.color = 'var(--color-sidebar-fg, var(--color-fg))';
                                e.currentTarget.style.backgroundColor = 'var(--color-sidebar-hover, var(--color-surface-muted))';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!childIsActive) {
                                e.currentTarget.style.color = 'var(--color-sidebar-fg-secondary, var(--color-fg-secondary))';
                                e.currentTarget.style.backgroundColor = '';
                              }
                            }}
                          >
                            <ChildIcon
                              size={16}
                              strokeWidth={1.5}
                              className="shrink-0"
                              style={{ color: iconColor(childIsActive) }}
                            />
                            {child.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // Regular nav items (no children)
            const link = (
              <Link
                key={item.label}
                href={item.href}
                className={cn(
                  'flex items-center rounded-lg text-sm font-medium transition-colors group',
                  collapsed ? 'justify-center px-2 py-2' : 'gap-3 px-3 py-2',
                )}
                style={{
                  color: isActive
                    ? 'var(--color-sidebar-fg, var(--color-fg))'
                    : 'var(--color-sidebar-fg-secondary, var(--color-fg-secondary))',
                  backgroundColor: isActive
                    ? 'var(--color-sidebar-active, var(--color-surface-muted))'
                    : undefined,
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = 'var(--color-sidebar-fg, var(--color-fg))';
                    e.currentTarget.style.backgroundColor = 'var(--color-sidebar-hover, var(--color-surface-muted))';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = 'var(--color-sidebar-fg-secondary, var(--color-fg-secondary))';
                    e.currentTarget.style.backgroundColor = '';
                  }
                }}
              >
                <Icon
                  size={20}
                  strokeWidth={1.5}
                  className="shrink-0"
                  style={{ color: iconColor(isActive) }}
                />
                {!collapsed && <span>{item.label}</span>}
              </Link>
            );
            return collapsed ? (
              <Tooltip key={item.label} content={item.label} position="right">
                {link}
              </Tooltip>
            ) : (
              link
            );
          })}

        </nav>

        {/* Admin profile footer with sign-out dropdown */}
        <div
          className="absolute bottom-0 left-0 right-0"
          style={{
            borderTop: '1px solid var(--color-sidebar-border, var(--color-edge-strong))',
            backgroundColor: 'var(--color-sidebar-bg, var(--color-surface))',
          }}
        >
          <div className={cn('p-2', collapsed && 'p-2')}>
            <Dropdown
              position="top"
              align="left"
              className="w-full"
              items={[
                ...(showModeToggle ? [{
                  label: isDark ? 'Light mode' : 'Dark mode',
                  icon: isDark
                    ? <Sun size={16} strokeWidth={1.5} />
                    : <Moon size={16} strokeWidth={1.5} />,
                  onClick: () => setTheme(isDark ? 'light' : 'dark'),
                },
                { type: 'divider' }] : []),
                {
                  label: 'Sign out',
                  icon: <LogOut size={16} strokeWidth={1.5} />,
                  destructive: true,
                  onClick: () => router.delete(admin_sign_out_path()),
                },
              ]}
              trigger={
                collapsed ? (
                  <div
                    className="flex items-center justify-center rounded-lg p-1.5 transition-colors cursor-pointer"
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-sidebar-hover, var(--color-surface-muted))'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                  >
                    <Avatar
                      src={admin?.avatar_url}
                      alt={admin?.email}
                      size="sm"
                    />
                  </div>
                ) : (
                  <div
                    className="flex items-center gap-3 rounded-lg px-2 py-1.5 transition-colors cursor-pointer"
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-sidebar-hover, var(--color-surface-muted))'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                  >
                    <Avatar
                      src={admin?.avatar_url}
                      alt={admin?.email}
                      size="sm"
                    />
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-sm font-medium truncate"
                        style={{ color: 'var(--color-sidebar-fg, var(--color-fg))' }}
                      >
                        {admin?.email ?? 'Admin'}
                      </p>
                    </div>
                    <ChevronsUpDown
                      size={16}
                      strokeWidth={1.5}
                      className="shrink-0"
                      style={{ color: 'var(--color-sidebar-fg-tertiary, var(--color-fg-tertiary))' }}
                    />
                  </div>
                )
              }
            />
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={cn('transition-all duration-200', mainPadding)}>
        {/* Mobile top bar */}
        <header className="lg:hidden flex items-center h-16 px-4 border-b border-edge-strong bg-surface">
          <button
            type="button"
            className="text-fg-secondary hover:text-fg"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={24} strokeWidth={2} />
          </button>
          <span className="ml-4 text-lg font-semibold text-fg">{appName}</span>
        </header>

        <main className="max-w-5xl mx-auto py-8 px-4 sm:px-6 lg:px-8">{children}</main>
      </div>
    </div>
  );
}
